import type { CustomerInfoVO } from '@/services/ant-design-pro/typings';
import { Modal, Tabs } from 'antd';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import AutoAuditBatchPanel from './AutoAuditBatchPanel';
import OurContactBatchPanel from './OurContactBatchPanel';
import CustomerContactBatchPanel from './CustomerContactBatchPanel';
import type { BatchScenarioPanelHandle, ScenarioKey } from './types';
import ValuationBatchPanel from './ValuationBatchPanel';

interface CustomerAmountPrecisionBatchModalProps {
  /** 是否显示批量编辑弹窗。 */
  visible: boolean;
  /** 列表勾选的客户，统一修改只应用于这些客户。 */
  customers: CustomerInfoVO[];
  /** 未保存任何配置时关闭弹窗。 */
  onCancel: () => void;
  /** 已成功修改配置后关闭并刷新客户列表。 */
  onSuccess: () => void;
}

/** 复用客户批量编辑入口，各页签独立提交我方联系人场景或客户方通讯资料。 */
const CustomerAmountPrecisionBatchModal: React.FC<
  CustomerAmountPrecisionBatchModalProps
> = ({ visible, customers, onCancel, onSuccess }) => {
  const [scenario, setScenario] = useState<ScenarioKey>('autoAudit');
  const [hasChanges, setHasChanges] = useState(false);
  const [showOk, setShowOk] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const autoAuditRef = useRef<BatchScenarioPanelHandle>(null);
  const valuationRef = useRef<BatchScenarioPanelHandle>(null);
  const ourContactRef = useRef<BatchScenarioPanelHandle>(null);
  const customerContactRef = useRef<BatchScenarioPanelHandle>(null);

  useEffect(() => {
    if (!visible) return;
    setScenario('autoAudit');
    setHasChanges(false);
    setShowOk(true);
    setSubmitting(false);
  }, [visible]);

  const handleDirty = useCallback(() => {
    setHasChanges(true);
  }, []);

  const handleFooterChange = useCallback(
    (state: { showOk: boolean; submitting: boolean }) => {
      setShowOk(state.showOk);
      setSubmitting(state.submitting);
    },
    [],
  );

  const closeModal = () => {
    if (hasChanges) onSuccess();
    else onCancel();
  };

  /** 提交当前页签，全部成功才关闭，部分失败由页内展示并保留输入。 */
  const handleOk = async () => {
    const panel = {
      autoAudit: autoAuditRef.current,
      valuation: valuationRef.current,
      ourContact: ourContactRef.current,
      customerContact: customerContactRef.current,
    }[scenario];
    const shouldClose = await panel?.submit();
    if (shouldClose) onSuccess();
  };

  return (
    <Modal
      title={`批量编辑客户参数（已选 ${customers.length} 个）`}
      open={visible}
      onCancel={closeModal}
      onOk={handleOk}
      confirmLoading={submitting}
      okText="确认修改"
      okButtonProps={{
        style: showOk ? undefined : { display: 'none' },
      }}
      cancelText="关闭"
      width={680}
      styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
      centered
      maskClosable={false}
      destroyOnClose
    >
      <Tabs
        activeKey={scenario}
        onChange={(key) => setScenario(key as ScenarioKey)}
        items={[
          {
            key: 'autoAudit',
            label: '自动审批',
            children: (
              <AutoAuditBatchPanel
                ref={autoAuditRef}
                customers={customers}
                active={visible && scenario === 'autoAudit'}
                onDirty={handleDirty}
                onFooterChange={handleFooterChange}
              />
            ),
          },
          {
            key: 'valuation',
            label: '测算开关',
            children: (
              <ValuationBatchPanel
                ref={valuationRef}
                customers={customers}
                active={visible && scenario === 'valuation'}
                onDirty={handleDirty}
                onFooterChange={handleFooterChange}
              />
            ),
          },
          {
            key: 'ourContact',
            label: '我方联系人',
            children: (
              <OurContactBatchPanel
                ref={ourContactRef}
                customers={customers}
                active={visible && scenario === 'ourContact'}
                onDirty={handleDirty}
                onFooterChange={handleFooterChange}
              />
            ),
          },
          {
            key: 'customerContact',
            label: '客户联系人',
            children: (
              <CustomerContactBatchPanel
                ref={customerContactRef}
                customers={customers}
                active={visible && scenario === 'customerContact'}
                onDirty={handleDirty}
                onFooterChange={handleFooterChange}
              />
            ),
          },
        ]}
      />
    </Modal>
  );
};

export default CustomerAmountPrecisionBatchModal;
